import { supabase } from "@/integrations/supabase/client"

// Now using integrated Supabase client - no need for environment variables
const useRealSupabase = true

export interface SharedQuilt {
  id: string
  title: string
  data: any // QuiltData
  summary: {
    estimatedCost?: number
    blocksAcross: number
    blocksDown: number
    quiltSize?: { width: number; height: number }
  }
  created_at: string
  share_id: string
}

export const sharedQuiltsService = {
  async getRecentSharedQuilts(limit = 3): Promise<SharedQuilt[]> {
    if (useRealSupabase && supabase) {
      try {
        const { data, error } = await supabase
          .from('shared_quilts')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit)
        
        if (error) throw error
        return (data || []).map(item => ({
          ...item,
          data: item.data as any,
          summary: item.summary as SharedQuilt['summary']
        }))
      } catch (error) {
        console.error('Supabase error, falling back to mock data:', error)
        return getMockSharedQuilts().slice(0, limit)
      }
    }
    
    // For demo purposes, return mock data until Supabase is properly configured
    return getMockSharedQuilts().slice(0, limit)
  },

  async saveSharedQuilt(quiltData: any, title: string, summary: any): Promise<string> {
    if (useRealSupabase && supabase) {
      try {
        const { data, error } = await supabase
          .from('shared_quilts')
          .insert([{
            title,
            data: quiltData,
            summary,
            created_at: new Date().toISOString(),
            share_id: Math.random().toString(36).substring(2, 15)
          }])
          .select('id')
          .single()
        
        if (error) throw error
        return data.id.toString()
      } catch (error) {
        console.error('Failed to save to Supabase:', error)
      }
    }
    
    // For demo mode, store in localStorage for persistence
    const mockId = Date.now().toString()
    
    const newMockQuilt = {
      id: mockId,
      title,
      data: quiltData,
      summary,
      created_at: new Date().toISOString(),
      share_id: Math.random().toString(36).substring(2, 15)
    }
    
    // Store in localStorage so plans persist across sessions
    try {
      const savedPlans = getSavedPlansFromStorage()
      savedPlans[mockId] = newMockQuilt
      localStorage.setItem('savedQuiltPlans', JSON.stringify(savedPlans))
    } catch (e) {
      console.error('Failed to store demo plan:', e)
    }
    
    return mockId
  },

  async getSharedQuiltById(id: string): Promise<SharedQuilt | null> {
    if (useRealSupabase && supabase) {
      try {
        const { data, error } = await supabase
          .from('shared_quilts')
          .select('*')
          .eq('id', id)
          .single()
        
        if (error) throw error
        return {
          ...data,
          data: data.data as any,
          summary: data.summary as SharedQuilt['summary']
        }
      } catch (error) {
        console.error('Failed to get shared quilt by ID:', error)
      }
    }
    
    // Check localStorage first for saved plans
    try {
      const savedPlans = getSavedPlansFromStorage()
      if (savedPlans[id]) {
        return savedPlans[id]
      }
    } catch (e) {
      console.error('Failed to get saved plan:', e)
    }
    
    // Fallback to mock data
    const mockQuilts = getMockSharedQuilts()
    return mockQuilts.find(q => q.id === id || q.share_id === id) || null
  }
}

// Helper function to get saved plans from localStorage
function getSavedPlansFromStorage(): Record<string, SharedQuilt> {
  try {
    const stored = localStorage.getItem('savedQuiltPlans')
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

// Mock data for demonstration
function getMockSharedQuilts(): SharedQuilt[] {
  return [
    {
      id: '1756328992974',
      title: 'My saved quilt plan',
      data: {
        purpose: 'bed-cover',
        bedSize: 'twin',
        mattressOverhang: { sides: 12, foot: 18, head: 0 },
        blockSize: '12x12',
        sashing: '2',
        cornerstones: true,
        border: '3',
        measurementSystem: 'yards',
        fabricWidthType: '44',
        fabricWidth: {
          blocks: 44,
          sashing: 44,
          cornerstones: 44,
          border: 44,
          backing: 44,
          binding: 44
        },
        fabricCostType: '12',
        bindingType: 'create-own',
        bindingWidth: '2.25',
        seam: '0.25'
      },
      summary: {
        estimatedCost: 156.75,
        blocksAcross: 6,
        blocksDown: 8,
        quiltSize: { width: 82, height: 106 }
      },
      created_at: new Date().toISOString(),
      share_id: 'saved-plan-abc123'
    },
    {
      id: '1',
      title: 'twin bed cover quilt',
      data: {
        purpose: 'bed-cover',
        bedSize: 'twin',
        mattressOverhang: { sides: 12, foot: 18, head: 0 },
        blockSize: '12x12',
        sashing: '2',
        cornerstones: true,
        border: '3',
        measurementSystem: 'yards',
        fabricWidthType: '44',
        fabricWidth: {
          blocks: 44,
          sashing: 44,
          cornerstones: 44,
          border: 44,
          backing: 44,
          binding: 44
        },
        fabricCostType: '12',
        bindingType: 'create-own',
        bindingWidth: '2.25',
        seam: '0.25'
      },
      summary: {
        estimatedCost: 156.75,
        blocksAcross: 6,
        blocksDown: 8,
        quiltSize: { width: 82, height: 106 }
      },
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(), // 3 hours ago
      share_id: 'twin-cozy-abc123'
    },
    {
      id: '2', 
      title: 'standard throw blanket quilt',
      data: {
        purpose: 'throw-blanket',
        throwSize: 'standard',
        blockSize: '10x10',
        sashing: 'none',
        cornerstones: false,
        border: '2',
        measurementSystem: 'yards',
        fabricWidthType: '44',
        fabricWidth: {
          blocks: 44,
          sashing: 44,
          cornerstones: 44,
          border: 44,
          backing: 44,
          binding: 44
        },
        fabricCostType: '10',
        bindingType: 'create-own',
        bindingWidth: '2.25',
        seam: '0.25'
      },
      summary: {
        estimatedCost: 89.50,
        blocksAcross: 5,
        blocksDown: 6,
        quiltSize: { width: 56, height: 66 }
      },
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(), // 8 hours ago
      share_id: 'modern-throw-xyz789'
    },
    {
      id: '3',
      title: 'queen bed cover quilt', 
      data: {
        purpose: 'bed-cover',
        bedSize: 'queen',
        mattressOverhang: { sides: 18, foot: 24, head: 12 },
        blockSize: '9x9',
        sashing: '1',
        cornerstones: true,
        border: '4',
        measurementSystem: 'yards',
        fabricWidthType: '44',
        fabricWidth: {
          blocks: 44,
          sashing: 44,
          cornerstones: 44,
          border: 44,
          backing: 44,
          binding: 44
        },
        fabricCostType: '12',
        bindingType: 'create-own',
        bindingWidth: '2.5',
        seam: '0.25'
      },
      summary: {
        estimatedCost: 324.80,
        blocksAcross: 9,
        blocksDown: 10,
        quiltSize: { width: 104, height: 124 }
      },
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(), // 12 hours ago
      share_id: 'queen-wedding-def456'
    },
    {
      id: '4',
      title: 'large throw blanket quilt',
      data: {
        purpose: 'throw-blanket',
        throwSize: 'large',
        blockSize: '6x6',
        sashing: '1',
        cornerstones: false,
        border: '3',
        measurementSystem: 'yards',
        fabricWidthType: '44',
        fabricWidth: {
          blocks: 44,
          sashing: 44,
          cornerstones: 44,
          border: 44,
          backing: 44,
          binding: 44
        },
        fabricCostType: '8',
        bindingType: 'pre-made',
        preMadeBindingPrice: 4,
        seam: '0.25'
      },
      summary: {
        estimatedCost: 78.25,
        blocksAcross: 10,
        blocksDown: 12,
        quiltSize: { width: 66, height: 78 }
      },
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 18).toISOString(), // 18 hours ago
      share_id: 'large-throw-ghi789'
    }
  ]
}